import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Mutation } from 'react-apollo';
import { useMutation as useApolloMutation, gql as apolloGql } from '@apollo/client';
import gql from 'graphql-tag';
import apolloClient from '../../lib/apolloClient';
import { useSnackbar } from 'notistack';
import { EnvelopeIcon, CheckBadgeIcon, ClockIcon } from '@heroicons/react/24/outline';
import VaultHeader from './VaultHeader';
import DocumentCard from './DocumentCard';
import DocumentGridItem from './DocumentGridItem';
import DocumentTable from './DocumentTable';
import DocumentUploadModal from './DocumentUploadModal';
import DocumentEditModal from './DocumentEditModal';
import DocumentDetailModal from './DocumentDetailModal';

const REQUEST_CAT_PLAN_EMAIL = apolloGql`
  mutation RequestCatPlanEmail {
    request_cat_plan_email {
      success
      message
    }
  }
`;

const RjApprovedPlanEntry = ({ catPlanFilesUnavailable, lastCatPlanDownloadedDate }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [requestCatPlanEmail, { loading }] = useApolloMutation(REQUEST_CAT_PLAN_EMAIL, { client: apolloClient });

  const handleEmailCopy = () => {
    if (catPlanFilesUnavailable) {
      setShowUnavailableModal(true);
      return;
    }
    requestCatPlanEmail().then(({ data }) => {
      const result = data?.request_cat_plan_email;
      if (result?.success) {
        enqueueSnackbar(result.message, { variant: 'success' });
      } else if (result?.message === 'catastrophic_plans_files_unavailable') {
        setShowUnavailableModal(true);
      } else {
        enqueueSnackbar(result?.message || intl.formatMessage({ id: 'succession.vault.error_generic' }), { variant: 'error' });
      }
    }).catch(() => {
      enqueueSnackbar(intl.formatMessage({ id: 'succession.vault.error_generic' }), { variant: 'error' });
    });
  };

  return (
    <>
      <div className="mb-4 p-4 rounded-lg border border-succession-success bg-succession-success-bg flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <CheckBadgeIcon className="h-6 w-6 text-succession-success flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.vault.rj_plan_title' })}
            </p>
            <p className="text-xs text-succession-text-secondary mt-0.5">
              {intl.formatMessage({ id: 'succession.vault.rj_plan_subtitle' })}
              {lastCatPlanDownloadedDate && ` ${intl.formatMessage({ id: 'succession.vault.rj_plan_last_requested' }, { date: new Date(lastCatPlanDownloadedDate).toLocaleDateString() })}`}
            </p>
          </div>
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-succession-bg-tertiary text-succession-text-secondary">
            plan_builder_catastrophic
          </span>
        </div>
        <button
          onClick={handleEmailCopy}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-succession-text-secondary border border-succession-border rounded-md hover:bg-succession-bg-primary transition-theme disabled:opacity-60 flex-shrink-0"
        >
          <EnvelopeIcon className="h-4 w-4" />
          {loading
            ? intl.formatMessage({ id: 'succession.vault.rj_plan_sending' })
            : intl.formatMessage({ id: 'succession.vault.rj_plan_email' })}
        </button>
      </div>

      {showUnavailableModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowUnavailableModal(false)}>
          <div className="bg-succession-bg-primary rounded-lg p-6 max-w-md w-full mx-4 shadow-succession-lg border border-succession-border" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-succession-text-primary mb-2">
              {intl.formatMessage({ id: 'succession.vault.rj_plan_unavailable_title' })}
            </h3>
            <p className="text-sm text-succession-text-secondary mb-4">
              {intl.formatMessage({ id: 'succession.vault.rj_plan_unavailable_desc' })}
            </p>
            <button
              onClick={() => setShowUnavailableModal(false)}
              className="px-4 py-2 text-sm font-medium text-white bg-succession-primary rounded-md hover:bg-succession-primary-hover transition-theme"
            >
              {intl.formatMessage({ id: 'succession.vault.close' })}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const DELETE_VAULT_DOCUMENT = gql`
  mutation DeleteVaultDocument($id: ID!) {
    delete_vault_document(id: $id)
  }
`;

const TOGGLE_VAULT_DOCUMENT_SHARING = gql`
  mutation ToggleVaultDocumentSharing($id: ID!, $shared_with_negotiating: Boolean!) {
    toggle_vault_document_sharing(id: $id, shared_with_negotiating: $shared_with_negotiating) {
      id
      shared_with_negotiating
    }
  }
`;

const Vault = ({ documents = [], refetch, is_rj, cat_plan_approved, catastrophic_plans_files_unavailable, last_cat_plan_downloaded_date }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [viewMode, setViewMode] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const filteredDocuments = useMemo(() => {
    let filtered = [...documents];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.title?.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query) ||
        doc.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(doc => doc.category === selectedCategory);
    }

    if (selectedSource !== 'all') {
      filtered = filtered.filter(doc => doc.source_type === selectedSource);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'date-asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'name-asc':
          return (a.title || '').localeCompare(b.title || '');
        case 'name-desc':
          return (b.title || '').localeCompare(a.title || '');
        case 'size-desc':
          return (b.file_size || 0) - (a.file_size || 0);
        case 'size-asc':
          return (a.file_size || 0) - (b.file_size || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [documents, searchQuery, selectedCategory, selectedSource, sortBy]);

  const handleUpload = () => setUploadModalOpen(true);

  const handleEdit = (document) => {
    setSelectedDocument(document);
    setEditModalOpen(true);
  };

  const handleView = (document) => {
    setSelectedDocument(document);
    setDetailModalOpen(true);
  };

  const handleDelete = (document, deleteMutation) => {
    if (window.confirm(intl.formatMessage({ id: 'succession.vault.delete_confirm' }, { title: document.title }))) {
      deleteMutation({
        variables: { id: document.id },
      }).then(() => {
        enqueueSnackbar(intl.formatMessage({ id: 'succession.vault.document_deleted' }), { variant: 'success' });
        refetch();
      }).catch(error => {
        enqueueSnackbar(intl.formatMessage({ id: 'succession.vault.document_delete_error' }, { error: error.message }), { variant: 'error' });
      });
    }
  };

  const handleToggleShare = (document, isShared) => {
    return (toggleMutation) => {
      toggleMutation({
        variables: {
          id: document.id,
          shared_with_negotiating: isShared,
        },
      }).then(() => {
        const msgId = isShared ? 'succession.vault.document_shared' : 'succession.vault.document_unshared';
        enqueueSnackbar(intl.formatMessage({ id: msgId }), { variant: 'success' });
        refetch();
      }).catch(error => {
        enqueueSnackbar(intl.formatMessage({ id: 'succession.vault.sharing_error' }, { error: error.message }), { variant: 'error' });
      });
    };
  };

  const handleDownload = (document) => {
    if (document.download_url) {
      window.open(document.download_url, '_blank');
    }
  };

  const categories = useMemo(() => {
    const cats = new Set(documents.map(d => d.category).filter(Boolean));
    return Array.from(cats);
  }, [documents]);

  const sources = useMemo(() => {
    const srcs = new Set(documents.map(d => d.source_type).filter(Boolean));
    return Array.from(srcs);
  }, [documents]);

  return (
    <div className="max-w-[1400px] mx-auto">
      <VaultHeader
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        selectedSource={selectedSource}
        onSourceChange={setSelectedSource}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onUploadClick={handleUpload}
        documentCount={filteredDocuments.length}
        categories={categories}
        sources={sources}
      />

      {is_rj && cat_plan_approved && (
        <RjApprovedPlanEntry
          catPlanFilesUnavailable={catastrophic_plans_files_unavailable}
          lastCatPlanDownloadedDate={last_cat_plan_downloaded_date}
        />
      )}

      {filteredDocuments.length === 0 ? (
        <div className="text-center py-16">
          <h6 className="text-lg font-semibold text-succession-text-secondary mb-2">
            {intl.formatMessage({ id: 'succession.vault.no_documents' })}
          </h6>
          <p className="text-sm text-succession-text-secondary">
            {documents.length === 0
              ? intl.formatMessage({ id: 'succession.vault.no_documents_start' })
              : intl.formatMessage({ id: 'succession.vault.no_documents_filter' })}
          </p>
        </div>
      ) : viewMode === 'list' ? (
        <DocumentTable
          documents={filteredDocuments}
          onEdit={handleEdit}
          onDelete={{
            mutation: DELETE_VAULT_DOCUMENT,
            onSuccess: () => {
              enqueueSnackbar(intl.formatMessage({ id: 'succession.vault.document_deleted' }), { variant: 'success' });
              refetch();
            },
          }}
          onDownload={handleDownload}
          onView={handleView}
          onToggleShare={{
            mutation: TOGGLE_VAULT_DOCUMENT_SHARING,
            onSuccess: () => refetch(),
          }}
        />
      ) : (
        <div className={`grid grid-cols-1 gap-4 mt-2 ${
          viewMode === 'card'
            ? 'sm:grid-cols-2 lg:grid-cols-3'
            : 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
        }`}>
          {filteredDocuments.map(document => (
            <div key={document.id}>
              {viewMode === 'card' ? (
                <Mutation mutation={TOGGLE_VAULT_DOCUMENT_SHARING}>
                  {toggleShare => (
                    <Mutation mutation={DELETE_VAULT_DOCUMENT}>
                      {deleteDoc => (
                        <DocumentCard
                          document={document}
                          onEdit={() => handleEdit(document)}
                          onDelete={() => handleDelete(document, deleteDoc)}
                          onDownload={() => handleDownload(document)}
                          onView={() => handleView(document)}
                          onToggleShare={(doc, isShared) => handleToggleShare(doc, isShared)(toggleShare)}
                        />
                      )}
                    </Mutation>
                  )}
                </Mutation>
              ) : (
                <Mutation mutation={DELETE_VAULT_DOCUMENT}>
                  {deleteDoc => (
                    <DocumentGridItem
                      document={document}
                      onEdit={() => handleEdit(document)}
                      onDelete={() => handleDelete(document, deleteDoc)}
                      onDownload={() => handleDownload(document)}
                      onView={() => handleView(document)}
                    />
                  )}
                </Mutation>
              )}
            </div>
          ))}
        </div>
      )}

      <DocumentUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={() => {
          setUploadModalOpen(false);
          refetch();
        }}
      />

      {selectedDocument && (
        <>
          <DocumentEditModal
            open={editModalOpen}
            document={selectedDocument}
            onClose={() => {
              setEditModalOpen(false);
              setSelectedDocument(null);
            }}
            onSuccess={() => {
              setEditModalOpen(false);
              setSelectedDocument(null);
              refetch();
            }}
          />

          <Mutation mutation={DELETE_VAULT_DOCUMENT}>
            {deleteDoc => (
              <DocumentDetailModal
                open={detailModalOpen}
                document={selectedDocument}
                onClose={() => {
                  setDetailModalOpen(false);
                  setSelectedDocument(null);
                }}
                onEdit={() => {
                  setDetailModalOpen(false);
                  handleEdit(selectedDocument);
                }}
                onDelete={() => handleDelete(selectedDocument, deleteDoc)}
                onDownload={() => handleDownload(selectedDocument)}
              />
            )}
          </Mutation>
        </>
      )}
    </div>
  );
};

Vault.propTypes = {
  documents: PropTypes.array,
  refetch: PropTypes.func.isRequired,
  is_rj: PropTypes.bool,
  cat_plan_approved: PropTypes.bool,
  catastrophic_plans_files_unavailable: PropTypes.bool,
  last_cat_plan_downloaded_date: PropTypes.string,
};

export default Vault;
